import React, { useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import { cls, componentWrapper } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import Transition from '../transition';
import IconErrorCircle from '../icon/IconErrorCircle';
import IconWarnCircle from '../icon/IconWarnCircle';
import IconSuccessCircle from '../icon/IconSuccessCircle';
import Portal from '../portal';
import { toast } from './methods';
import Loading from '../loading';
import { usePreventBodyScroll } from '../_helpers';
export * from './methods';
var Toast = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      getContainer = props.getContainer,
      _props$visible = props.visible,
      visible = _props$visible === void 0 ? false : _props$visible,
      onClose = props.onClose,
      close = props.close,
      _props$type = props.type,
      type = _props$type === void 0 ? 'info' : _props$type,
      icon = props.icon,
      _props$layout = props.layout,
      layout = _props$layout === void 0 ? 'vertical' : _props$layout,
      _props$duration = props.duration,
      duration = _props$duration === void 0 ? 3000 : _props$duration,
      _props$transitionDura = props.transitionDuration,
      transitionDuration = _props$transitionDura === void 0 ? 300 : _props$transitionDura,
      content = props.content,
      _props$loading = props.loading,
      loading = _props$loading === void 0 ? false : _props$loading,
      loadingIcon = props.loadingIcon,
      _props$disableBodyTou = props.disableBodyTouch,
      disableBodyTouch = _props$disableBodyTou === void 0 ? false : _props$disableBodyTou,
      loadingInner = props.loadingInner,
      _props$direction = props.direction,
      direction = _props$direction === void 0 ? 'center' : _props$direction,
      typeIconMap = props.typeIconMap,
      initialBodyOverflow = props.initialBodyOverflow;
  var closeTimerRef = useRef();
  var domRef = useRef(null);
  var isInitialMount = useRef(false);
  var hasType = type && type !== 'info';
  useImperativeHandle(ref, function () {
    return {
      dom: domRef.current
    };
  });
  usePreventBodyScroll(visible, disableBodyTouch, initialBodyOverflow);

  function handleClose() {
    if (close) {
      close();
    }
  }

  function startCloseTimer() {
    if (duration) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = window.setTimeout(function () {
        handleClose();
      }, duration);
    }
  }

  function handleClickMask(e) {
    if (disableBodyTouch) {
      e.preventDefault();
    }
  }

  function getTypeIcons() {
    var map = typeIconMap || {};
    var defaultMap = {
      success: /*#__PURE__*/React.createElement(IconSuccessCircle, null),
      error: /*#__PURE__*/React.createElement(IconErrorCircle, null),
      warn: /*#__PURE__*/React.createElement(IconWarnCircle, null)
    };
    return map[type] || defaultMap[type] || null;
  }

  useEffect(function () {
    var mounted = isInitialMount.current;

    if (visible) {
      startCloseTimer();
    } else {
      clearTimeout(closeTimerRef.current);

      if (mounted) {
        setTimeout(function () {
          onClose && onClose();
        }, transitionDuration);
      }
    }

    isInitialMount.current = true;
  }, [visible]);
  useEffect(function () {
    startCloseTimer();
  }, [duration]);

  function renderComponent(prefixClass) {
    var _cls;

    return /*#__PURE__*/React.createElement("div", {
      className: cls(prefixClass + "-wrapper", "from-" + direction, {
        'no-event': !disableBodyTouch
      }),
      onClick: handleClickMask
    }, /*#__PURE__*/React.createElement("div", {
      className: cls(prefixClass + "-inner", layout, (_cls = {}, _cls[prefixClass + "-inner-mixin"] = (loading || hasType || icon) && layout === 'vertical', _cls))
    }, loading && /*#__PURE__*/React.createElement("div", {
      className: prefixClass + "-loading-wrapper"
    }, loadingIcon !== void 0 ? loadingIcon : /*#__PURE__*/React.createElement(Loading, {
      className: "loading-icon",
      type: "arc",
      stroke: 2,
      radius: loadingInner ? 19 : 11
    }), loadingInner ? /*#__PURE__*/React.createElement("span", {
      className: prefixClass + "-loading-inner"
    }, loadingInner) : null), !loading && hasType ? getTypeIcons() : null, !loading && !hasType && icon ? icon : null, content ? /*#__PURE__*/React.createElement("div", {
      className: prefixClass + "-content"
    }, content) : null));
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref) {
    var prefixCls = _ref.prefixCls;
    return /*#__PURE__*/React.createElement(Portal, {
      getContainer: getContainer
    }, /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-toast", 'all-border-box', className),
      ref: domRef
    }, /*#__PURE__*/React.createElement(Transition, {
      in: visible,
      timeout: transitionDuration,
      type: "fade"
    }, renderComponent(prefixCls + "-toast"))));
  });
});
export function methodsGenerator(Comp) {
  return {
    /**
     * 展示常规提示框
     * @en Show regular toast
     * @param {string | ToastProps} config Configuration
     * @returns {{ update: (config: ToastProps) => void; close: () => void }}
     */
    toast: toast(Comp),

    /**
     * 展示常规提示框，同 Toast.toast
     * @en Show regular toast, the same as Toast.toast
     * @param {string | ToastProps} config Configuration
     * @returns {{ update: (config: ToastProps) => void; close: () => void }}
     */
    info: toast(Comp, 'info'),

    /**
     * 展示成功提示框(含成功icon)
     * @en Show success prompt toast (including success icon)
     * @param {string | ToastProps} config Configuration
     * @returns {{ update: (config: ToastProps) => void; close: () => void }}
     */
    success: toast(Comp, 'success'),

    /**
     * 展示错误提示框(含错误icon)
     * @en Display error prompt toast (including error icon)
     * @param {string | ToastProps} config Configuration
     * @returns {{ update: (config: ToastProps) => void; close: () => void }}
     */
    error: toast(Comp, 'error'),

    /**
     * 展示加载中提示框(含加载中icon)
     * @en Display loading prompt toast (including loading icon)
     * @param {string | ToastProps} config Configuration
     * @returns {{ update: (config: ToastProps) => void; close: () => void }}
     */
    loading: toast(Comp, 'loading'),

    /**
     * 展示警告提示框(含警告icon)
     * @en Display warning prompt toast (including warning icon)
     * @param {string | ToastProps} config Configuration
     * @returns {{ update: (config: ToastProps) => void; close: () => void }}
     */
    warn: toast(Comp, 'warn')
  };
}
/**
 * 轻提示组件，支持各个场景下调用方法。
 * @en The toast component, supports calling methods in various scenarios.
 * @type 反馈
 * @type_en Feedback
 * @name 轻提示
 * @name_en Toast
 */

export default componentWrapper(Toast, methodsGenerator(Toast));